/**
 * Lifecycle-email → admin-panel landing telemetry.
 * WP appends ss_from=email&ss_mail=…&ss_cta=… on CTAs; we record once and strip params.
 */

export const EMAIL_LANDING_STORAGE_KEY = 'supersee_email_landings';
const EMAIL_LANDING_MAX = 100;

export interface EmailLandingEvent {
  at: string;
  path: string;
  mail: string;
  cta: string;
}

export interface EmailLandingQueryLike {
  get(name: string): string | null;
}

function readParam(params: EmailLandingQueryLike | URLSearchParams, name: string): string {
  const raw = (params.get(name) || '').trim();
  return raw;
}

/** True when this navigation came from a tracked lifecycle email CTA. */
export function isEmailLanding(params: EmailLandingQueryLike | URLSearchParams): boolean {
  return readParam(params, 'ss_from').toLowerCase() === 'email' && !!readParam(params, 'ss_mail');
}

export function parseEmailLanding(
  params: EmailLandingQueryLike | URLSearchParams,
  path: string
): EmailLandingEvent | null {
  if (!isEmailLanding(params)) {
    return null;
  }
  return {
    at: new Date().toISOString(),
    path: path || '/',
    mail: readParam(params, 'ss_mail').toLowerCase(),
    cta: readParam(params, 'ss_cta').toLowerCase(),
  };
}

/** Persist a short ring buffer for local debugging / support. */
export function rememberEmailLanding(storage: Storage, event: EmailLandingEvent): void {
  let list: EmailLandingEvent[] = [];
  try {
    const raw = storage.getItem(EMAIL_LANDING_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        list = parsed;
      }
    }
  } catch {
    list = [];
  }
  list.push(event);
  storage.setItem(EMAIL_LANDING_STORAGE_KEY, JSON.stringify(list.slice(-EMAIL_LANDING_MAX)));
}

/** Push to GTM dataLayer when present (production). */
export function pushEmailLandingToDataLayer(event: EmailLandingEvent): void {
  const w = window as Window & { dataLayer?: Record<string, unknown>[] };
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push({
    event: 'email_admin_landing',
    ss_from: 'email',
    ss_mail: event.mail,
    ss_cta: event.cta,
    ss_path: event.path,
  });
}

/**
 * Remove ss_from / ss_mail / ss_cta from the current URL without a navigation.
 */
export function stripEmailLandingParamsFromUrl(): void {
  if (typeof window === 'undefined' || !window.history?.replaceState) {
    return;
  }
  const url = new URL(window.location.href);
  let changed = false;
  ['ss_from', 'ss_mail', 'ss_cta'].forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });
  if (changed) {
    const next = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState(window.history.state, '', next);
  }
}

/**
 * Handle one NavigationEnd URL: record + GTM + strip.
 * Returns the event when tracked, else null.
 */
export function captureEmailAdminLanding(urlAfterRedirects: string, storage: Storage = localStorage): EmailLandingEvent | null {
  if (typeof window === 'undefined') {
    return null;
  }
  let params: URLSearchParams;
  try {
    params = new URL(urlAfterRedirects, window.location.origin).searchParams;
  } catch {
    const q = urlAfterRedirects.includes('?') ? urlAfterRedirects.split('?')[1] : '';
    params = new URLSearchParams(q.split('#')[0]);
  }
  const pathOnly = (urlAfterRedirects.split('?')[0] || '/').split('#')[0] || '/';
  const event = parseEmailLanding(params, pathOnly);
  if (!event) {
    return null;
  }
  rememberEmailLanding(storage, event);
  pushEmailLandingToDataLayer(event);
  stripEmailLandingParamsFromUrl();
  return event;
}
